/* eslint-disable react/jsx-props-no-spreading */
// import * as AppPropTypes from '../../lib/PropTypes';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import { stringify } from 'query-string';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'wouter';

import { useResizeObserver } from '../../hooks/useObserver';

import { useMenu, useHeader, useSites, useSite } from '../../contexts/SiteContext';
import Button from '../buttons/Button';
import LocalesMenu from '../menus/LocalesMenu';
import MainMenu from '../menus/MainMenu';
import Link from '../partials/Link';

import styles from '../../../styles/headers/header.module.scss';

import logoSrc from '../../../img/urbania-logo.png';

const propTypes = {
    withoutMenu: PropTypes.bool,
    inverted: PropTypes.bool,
    loading: PropTypes.bool,
    rootDomain: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    withoutMenu: false,
    inverted: false,
    loading: false,
    rootDomain: null,
    className: null,
};

const HEADER_BG_SCROLL_THRESHOLD = 80;

const Header = ({ withoutMenu, inverted, loading, rootDomain, className }) => {
    const route = useUrlGenerator();
    const [pathname] = useLocation();

    const { mainMenu, mainLeftMenu, mainRightMenu, menuOpen, setMenuOpen, closeMenu } = useMenu();
    const { id: siteId = null } = useSite();
    const sites = useSites();

    const { headerHidden, setHeaderHidden, scrollDirection, setScrollDirection, headerDisabled } =
        useHeader();
    const [scrollY, setScrollY] = useState(0);

    const onClick = useCallback(() => {
        setMenuOpen((opened) => !opened);
    }, [setMenuOpen]);

    useEffect(() => {
        const { style: bodyStyle } = document.body;
        if (menuOpen) {
            bodyStyle.setProperty('position', 'fixed');
            bodyStyle.setProperty('width', '100%');
            bodyStyle.setProperty('overflow-y', 'scroll');
        } else {
            bodyStyle.removeProperty('position');
            bodyStyle.removeProperty('width');
            bodyStyle.removeProperty('overflow-y');
        }
    }, [menuOpen]);

    useEffect(() => {
        const onScroll = () => {
            const { scrollY: windowScrollY } = window;
            const newScrollDirection = windowScrollY > scrollY ? 'down' : 'up';
            const isScrollingDown = newScrollDirection === 'down';
            const isBeyondThreshold = windowScrollY >= HEADER_BG_SCROLL_THRESHOLD;
            const shouldHide = isScrollingDown && isBeyondThreshold;
            if (!headerDisabled) {
                setHeaderHidden(shouldHide);
                setScrollDirection(newScrollDirection);
                setScrollY(windowScrollY);
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [
        headerHidden,
        setHeaderHidden,
        scrollY,
        setScrollY,
        scrollDirection,
        setScrollDirection,
        headerDisabled,
    ]);

    const {
        ref: innerRef,
        entry: { contentRect = null },
    } = useResizeObserver();

    const { width = 0, height = 0 } = contentRect || {};

    useEffect(() => {
        document.documentElement.style.setProperty('--headerHeight', `${height}px`);
    }, [height]);

    // Initial
    useEffect(() => {
        const { scrollY: windowScrollY } = window;
        if (windowScrollY >= HEADER_BG_SCROLL_THRESHOLD) {
            setScrollDirection('up');
        }
        setScrollY(windowScrollY);
    }, []);

    const menuItems = useMemo(
        () =>
            (mainMenu || []).map((it) => {
                const { url = null } = it || {};
                const queryParams = stringify(it.query || '', { arrayFormat: 'bracket' });
                const finalUrl = !isEmpty(queryParams) ? `${url}?${queryParams}` : url;
                return {
                    ...it,
                    active: pathname.includes(it.url),
                    url: rootDomain !== null ? `${rootDomain}${finalUrl}` : finalUrl,
                    external: rootDomain !== null,
                    target: rootDomain !== null ? '_self' : null,
                };
            }),
        [pathname, mainMenu, rootDomain],
    );

    const finalMenuItems = menuItems || [];
    const finalItemsLeft = finalMenuItems.filter(
        ({ id = null }) =>
            (mainLeftMenu.find(({ id: leftId = null }) => leftId === id) || null) !== null,
    );
    const finalItemsRight = finalMenuItems.filter(
        ({ id = null }) =>
            (mainRightMenu.find(({ id: leftId = null }) => leftId === id) || null) !== null,
    );

    const finalSites = useMemo(
        () =>
            width < 768 ? sortBy(sites, ({ id = null } = {}) => (siteId === id ? -1 : 0)) : sites,
        [sites, siteId, width],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isMenuOpen]: menuOpen,
                    [styles.isHeaderHidden]: headerHidden,
                    [styles.isHeaderOpaque]: scrollY > HEADER_BG_SCROLL_THRESHOLD && !headerHidden,
                },
            ])}
            ref={innerRef}
        >
            <div className={styles.background} />
            <div className={styles.inner}>
                <Link
                    className={styles.logo}
                    href={rootDomain || route('home')}
                    external={rootDomain !== null}
                    target={rootDomain !== null ? '_self' : null}
                    onClick={closeMenu}
                >
                    <img
                        draggable="false"
                        className={classNames([
                            styles.logoImg,
                            {
                                [styles.inverted]: inverted,
                                [styles.hidden]: menuOpen,
                                [styles.loading]: loading,
                            },
                        ])}
                        src={logoSrc}
                        alt="URBANIA"
                    />
                </Link>
                {!withoutMenu ? (
                    <div className={styles.menu}>
                        {finalItemsLeft.map(({ id, label = null, url = null, active = false }) => (
                            <Link
                                key={`left-${id}-${label}`}
                                className={classNames([
                                    styles.menuItem,
                                    {
                                        [styles.active]: active,
                                    },
                                ])}
                                href={url}
                                external={rootDomain !== null}
                                target={rootDomain !== null ? '_self' : null}
                            >
                                <span>{label}</span>
                            </Link>
                        ))}
                        {finalItemsRight.map(
                            ({ id, label = null, url = null, active = false }, i) => (
                                <Link
                                    key={`right-${id}-${label}`}
                                    className={classNames([
                                        styles.menuItem,
                                        {
                                            [styles.active]: active,
                                            [styles.break]: i === 0,
                                        },
                                    ])}
                                    href={url}
                                    external={rootDomain !== null}
                                    target={rootDomain !== null ? '_self' : null}
                                >
                                    <span>{label}</span>
                                </Link>
                            ),
                        )}
                    </div>
                ) : null}
                <div className={styles.actions}>
                    <LocalesMenu className={styles.localesMenu} />
                    <Button className={styles.menuButton} onClick={onClick} ariaLabel="Menu" />
                </div>
            </div>
            <MainMenu
                className={styles.mainMenu}
                items={finalMenuItems}
                opened={menuOpen}
                onSafeClick={closeMenu}
                onItemClick={closeMenu}
            />
            <div className={classNames([styles.sites, { [styles.hidden]: menuOpen }])}>
                {(finalSites || []).map(
                    ({ id = null, name = null, label = null, domain = null } = {}) => (
                        <a
                            className={classNames([
                                styles.site,
                                { [styles.current]: siteId === id },
                            ])}
                            href={`${domain.replace(/\/$/, '')}${pathname}`}
                            target="_self"
                        >
                            {label || name}
                        </a>
                    ),
                )}
            </div>
        </div>
    );
};

Header.propTypes = propTypes;
Header.defaultProps = defaultProps;

export default Header;
